import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { Capacitor } from '@capacitor/core';

interface SystemMediaQueriesContextType {
  isSmall: boolean;
  isMedium: boolean;
  isLarge: boolean;
  isLandscape: boolean;
}

export const SystemMediaQueriesContext =
  createContext<SystemMediaQueriesContextType>({
    isSmall: false,
    isMedium: false,
    isLarge: false,
    isLandscape: false,
  });

interface SystemMediaQueriesProviderProps {
  children: React.ReactNode;
}

export const SystemMediaQueriesProvider: React.FC<
  SystemMediaQueriesProviderProps
> = ({ children }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    // Only use orientation detection on mobile platforms
    if (Capacitor.isNativePlatform()) {
      const handleOrientationChange = () => {
        // Use the modern screen.orientation API
        const isLandscapeOrientation =
          window.screen.orientation.type.includes('landscape');
        setIsLandscape(isLandscapeOrientation);
      };

      // Initial orientation check
      handleOrientationChange();

      // Listen for orientation changes
      window.screen.orientation.addEventListener(
        'change',
        handleOrientationChange,
      );

      return () => {
        window.screen.orientation.removeEventListener(
          'change',
          handleOrientationChange,
        );
      };
    }
  }, []);

  const value = {
    isSmall,
    isMedium,
    isLarge,
    isLandscape,
  };

  return (
    <SystemMediaQueriesContext.Provider value={value}>
      {children}
    </SystemMediaQueriesContext.Provider>
  );
};

export const useIsSmallScreen = (): boolean => {
  const context = useContext(SystemMediaQueriesContext);
  if (context === undefined) {
    throw new Error(
      'useIsSmallScreen must be used within a SystemMediaQueriesProvider',
    );
  }
  return context.isSmall;
};

export const useIsMediumScreen = (): boolean => {
  const context = useContext(SystemMediaQueriesContext);
  if (context === undefined) {
    throw new Error(
      'useIsMediumScreen must be used within a SystemMediaQueriesProvider',
    );
  }
  return context.isMedium;
};

export const useIsLargeScreen = (): boolean => {
  const context = useContext(SystemMediaQueriesContext);
  if (context === undefined) {
    throw new Error(
      'useIsLargeScreen must be used within a SystemMediaQueriesProvider',
    );
  }
  return context.isLarge;
};

export const useIsLandscapeOrientation = (): boolean => {
  const context = useContext(SystemMediaQueriesContext);
  if (context === undefined) {
    throw new Error(
      'useIsLandscapeOrientation must be used within a SystemMediaQueriesProvider',
    );
  }
  return context.isLandscape;
};
